import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useEffect,
  useRef,
} from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useStoreAuth } from "./StoreAuthContext";
import { getStoreProductStockCap, getStoreCartLineUnitPrice } from "../../utils/storeUtils";

const WISHLIST_STORAGE_KEY = "alramlaa_wishlist";
const CART_STORAGE_KEY = "alramlaa_cart";

function loadLocalCart() {
  try {
    const raw = localStorage.getItem(CART_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.map((item) => ({
      ...item,
      image: typeof item.image === "object" ? item.image : { url: item.imageUrl || "" },
      selectedColor: item.selectedColor,
      selectedColorHex: item.selectedColorHex,
      price_override: item.price_override,
      selectedWidth: item.selectedWidth ?? null,
      selectedHeight: item.selectedHeight ?? null,
      widthAdjustment: item.widthAdjustment ?? "none",
      heightAdjustment: item.heightAdjustment ?? "none",
      chainType: item.chainType ?? "",
    }));
  } catch {
    return [];
  }
}

function saveLocalCart(items) {
  try {
    const toSave = items.map((item) => ({
      _id: item._id,
      name: item.name,
      arName: item.arName,
      price: item.price,
      discount_percentage: item.discount_percentage,
      original_price: item.original_price,
      final_price: item.final_price,
      quantity: item.quantity,
      image: item.image || { url: item.imageUrl || "" },
      selectedColor: item.selectedColor,
      selectedColorHex: item.selectedColorHex,
      price_override: item.price_override,
      selectedWidth: item.selectedWidth ?? null,
      selectedHeight: item.selectedHeight ?? null,
      widthAdjustment: item.widthAdjustment ?? "none",
      heightAdjustment: item.heightAdjustment ?? "none",
      chainType: item.chainType ?? "",
      min_delivery_days: item.min_delivery_days,
      max_delivery_days: item.max_delivery_days,
    }));
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(toSave));
  } catch (_) {}
}

function loadLocalWishlist() {
  try {
    const raw = localStorage.getItem(WISHLIST_STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function saveLocalWishlist(items) {
  try {
    localStorage.setItem(WISHLIST_STORAGE_KEY, JSON.stringify(items));
  } catch (_) {}
}

const StoreCartContext = createContext(null);

export function StoreCartProvider({ children }) {
  const [cart, setCart] = useState(loadLocalCart);
  const [wishlist, setWishlist] = useState([]);
  const [wishlistSyncing, setWishlistSyncing] = useState(false);
  const { isAuthenticated, loading: authLoading } = useStoreAuth();
  const didFetchWishlist = useRef(false);
  const prevAuth = useRef(false);

  // Guest: persist to localStorage when wishlist changes
  useEffect(() => {
    if (!isAuthenticated && wishlist.length >= 0) {
      saveLocalWishlist(wishlist);
    }
  }, [isAuthenticated, wishlist]);

  // Authenticated: fetch wishlist once when auth is ready. On logout, persist current list to local.
  useEffect(() => {
    if (authLoading) return;
    if (isAuthenticated) {
      if (!didFetchWishlist.current) {
        didFetchWishlist.current = true;
        setWishlistSyncing(true);
        axios
          .get("/wishlist")
          .then(({ data }) => {
            setWishlist(Array.isArray(data) ? data : []);
          })
          .catch(() => setWishlist([]))
          .finally(() => setWishlistSyncing(false));
      }
    } else {
      if (prevAuth.current) {
        saveLocalWishlist(wishlist);
        prevAuth.current = false;
      }
      didFetchWishlist.current = false;
      setWishlist(loadLocalWishlist());
    }
  }, [isAuthenticated, authLoading]); // eslint-disable-line react-hooks/exhaustive-deps

  // On login: merge local wishlist into server, then replace state with server list
  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    if (prevAuth.current === false && isAuthenticated) {
      prevAuth.current = true;
      const local = loadLocalWishlist();
      const localIds = local
        .map((p) => p._id)
        .filter(Boolean);
      if (localIds.length > 0) {
        setWishlistSyncing(true);
        axios
          .get("/wishlist")
          .then(({ data: serverList }) => {
            const serverIds = (serverList || []).map((p) => p._id || p);
            const mergedIds = [...new Set([...serverIds, ...localIds])];
            return axios.put("/wishlist", { productIds: mergedIds });
          })
          .then(({ data }) => {
            setWishlist(Array.isArray(data) ? data : []);
            saveLocalWishlist([]);
          })
          .catch(() => {})
          .finally(() => setWishlistSyncing(false));
      }
    }
    if (!isAuthenticated) prevAuth.current = false;
  }, [isAuthenticated, authLoading]);

  const addToWishlist = useCallback(
    (product) => {
      if (!product?._id) return;
      if (isAuthenticated) {
        setWishlistSyncing(true);
        axios
          .post(`/wishlist/add/${product._id}`)
          .then(({ data }) => setWishlist(Array.isArray(data) ? data : []))
          .catch(() => {})
          .finally(() => setWishlistSyncing(false));
      } else {
        setWishlist((prev) => {
          if (prev.some((p) => p._id === product._id)) return prev;
          return [...prev, { ...product }];
        });
      }
    },
    [isAuthenticated]
  );

  const removeFromWishlist = useCallback(
    (productId) => {
      if (isAuthenticated) {
        setWishlistSyncing(true);
        axios
          .delete(`/wishlist/${productId}`)
          .then(({ data }) => setWishlist(Array.isArray(data) ? data : []))
          .catch(() => {})
          .finally(() => setWishlistSyncing(false));
      } else {
        setWishlist((prev) => prev.filter((p) => p._id !== productId));
      }
    },
    [isAuthenticated]
  );

  const toggleWishlist = useCallback(
    (product) => {
      if (!product?._id) return;
      const inList = wishlist.some((p) => p._id === product._id);
      if (inList) removeFromWishlist(product._id);
      else addToWishlist(product);
    },
    [wishlist, addToWishlist, removeFromWishlist]
  );

  const isInWishlist = useCallback(
    (productId) => wishlist.some((p) => p._id === productId),
    [wishlist]
  );

  useEffect(() => {
    saveLocalCart(cart);
  }, [cart]);

  const addToCart = useCallback((product, options = {}) => {
    const selectedColor = options.selectedColor;
    const selectedColorHex = options.selectedColorHex;
    const selectedWidth = options.selectedWidth ?? null;
    const selectedHeight = options.selectedHeight ?? null;
    const widthAdjustment = options.widthAdjustment ?? "none";
    const heightAdjustment = options.heightAdjustment ?? "none";
    const chainType = options.chainType ?? "";
    const priceOverride = options.priceOverride;
    const originalPriceOverride = options.originalPriceOverride;
    const discountPercentOverride = options.discountPercentOverride;
    const cap = getStoreProductStockCap(product);
    setCart((prev) => {
      const existing = prev.find(
        (item) =>
          item._id === product._id &&
          (item.selectedColor || "") === (selectedColor || "") &&
          (item.selectedWidth ?? null) === (selectedWidth ?? null) &&
          (item.selectedHeight ?? null) === (selectedHeight ?? null) &&
          (item.widthAdjustment || "none") === (widthAdjustment || "none") &&
          (item.heightAdjustment || "none") === (heightAdjustment || "none") &&
          (item.chainType || "") === (chainType || "")
      );
      if (existing) {
        const nextQty = existing.quantity + 1;
        if (cap !== null && nextQty > cap) {
          toast.error("Not enough stock available");
          return prev;
        }
        return prev.map((item) =>
          item._id === product._id &&
          (item.selectedColor || "") === (selectedColor || "") &&
          (item.selectedWidth ?? null) === (selectedWidth ?? null) &&
          (item.selectedHeight ?? null) === (selectedHeight ?? null) &&
          (item.widthAdjustment || "none") === (widthAdjustment || "none") &&
          (item.heightAdjustment || "none") === (heightAdjustment || "none") &&
          (item.chainType || "") === (chainType || "")
            ? { ...item, quantity: nextQty }
            : item
        );
      }
      if (cap !== null && cap < 1) {
        toast.error("Not enough stock available");
        return prev;
      }
      const firstImage = product.images?.[0] || product.image;
      return [
        ...prev,
        {
          ...product,
          image: firstImage || product.image,
          quantity: 1,
          selectedColor: selectedColor || undefined,
          selectedColorHex: selectedColorHex || undefined,
          selectedWidth,
          selectedHeight,
          widthAdjustment,
          heightAdjustment,
          chainType,
          price_override:
            Number.isFinite(Number(priceOverride)) && Number(priceOverride) >= 0
              ? Number(priceOverride)
              : undefined,
          original_price:
            Number.isFinite(Number(originalPriceOverride)) && Number(originalPriceOverride) >= 0
              ? Number(originalPriceOverride)
              : product.original_price,
          final_price:
            Number.isFinite(Number(priceOverride)) && Number(priceOverride) >= 0
              ? Number(priceOverride)
              : product.final_price,
          discount_percentage:
            Number.isFinite(Number(discountPercentOverride)) && Number(discountPercentOverride) >= 0
              ? Number(discountPercentOverride)
              : product.discount_percentage,
        },
      ];
    });
  }, []);

  const updateQuantity = useCallback((id, quantity, selectedColor, extraOptions = null) => {
    const parsed = Number(quantity || 0);
    const extra = extraOptions && typeof extraOptions === "object" ? extraOptions : {};
    const keyWidth = extra.selectedWidth ?? null;
    const keyHeight = extra.selectedHeight ?? null;
    const keyWAdj = extra.widthAdjustment ?? "none";
    const keyHAdj = extra.heightAdjustment ?? "none";
    const keyChain = extra.chainType ?? "";
    if (parsed <= 0) {
      setCart((prev) =>
        prev.filter(
          (item) =>
            !(
              item._id === id &&
              (item.selectedColor || "") === (selectedColor || "") &&
              (item.selectedWidth ?? null) === (keyWidth ?? null) &&
              (item.selectedHeight ?? null) === (keyHeight ?? null) &&
              (item.widthAdjustment || "none") === (keyWAdj || "none") &&
              (item.heightAdjustment || "none") === (keyHAdj || "none") &&
              (item.chainType || "") === (keyChain || "")
            )
        )
      );
      return;
    }
    setCart((prev) => {
      const line = prev.find(
        (item) =>
          item._id === id &&
          (item.selectedColor || "") === (selectedColor || "") &&
          (item.selectedWidth ?? null) === (keyWidth ?? null) &&
          (item.selectedHeight ?? null) === (keyHeight ?? null) &&
          (item.widthAdjustment || "none") === (keyWAdj || "none") &&
          (item.heightAdjustment || "none") === (keyHAdj || "none") &&
          (item.chainType || "") === (keyChain || "")
      );
      const cap = line ? getStoreProductStockCap(line) : null;
      const q = cap !== null ? Math.min(parsed, cap) : parsed;
      if (cap !== null && parsed > cap) {
        toast.error("Not enough stock available");
      }
      return prev.map((item) =>
        item._id === id &&
        (item.selectedColor || "") === (selectedColor || "") &&
        (item.selectedWidth ?? null) === (keyWidth ?? null) &&
        (item.selectedHeight ?? null) === (keyHeight ?? null) &&
        (item.widthAdjustment || "none") === (keyWAdj || "none") &&
        (item.heightAdjustment || "none") === (keyHAdj || "none") &&
        (item.chainType || "") === (keyChain || "")
          ? { ...item, quantity: q }
          : item
      );
    });
  }, []);

  const cartTotal = useMemo(
    () =>
      cart.reduce(
        (sum, item) =>
          sum + getStoreCartLineUnitPrice(item) * Number(item.quantity || 0),
        0
      ),
    [cart]
  );

  const cartCount = useMemo(
    () => cart.reduce((sum, item) => sum + Number(item.quantity || 0), 0),
    [cart]
  );

  const wishlistCount = useMemo(() => wishlist.length, [wishlist]);

  const value = useMemo(
    () => ({
      cart,
      setCart,
      addToCart,
      updateQuantity,
      cartTotal,
      cartCount,
      wishlist,
      setWishlist,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isInWishlist,
      wishlistCount,
      wishlistSyncing,
    }),
    [
      cart,
      addToCart,
      updateQuantity,
      cartTotal,
      cartCount,
      wishlist,
      addToWishlist,
      removeFromWishlist,
      toggleWishlist,
      isInWishlist,
      wishlistCount,
      wishlistSyncing,
    ]
  );

  return (
    <StoreCartContext.Provider value={value}>
      {children}
    </StoreCartContext.Provider>
  );
}

export function useStoreCart() {
  const ctx = useContext(StoreCartContext);
  if (!ctx) {
    throw new Error("useStoreCart must be used within StoreCartProvider");
  }
  return ctx;
}
